import { useMemo } from 'react';
import { ComboboxSelect } from '@/components/ui/combobox-select';
import { useStore } from '@/lib/store';

/**
 * App-wide searchable pickers for matters and clients.
 *
 * Every place a user selects a matter or client should use these instead of
 * a plain <Select>: they are searchable, use the canonical
 * "Group - Name" matter label, hide inactive matters by default, and match
 * on related text (a matter's client names, a client's email/VAT number).
 *
 * Values are numeric ids (null = nothing selected). Lists default to the
 * global store; pass `matters`/`clients` to restrict the choices.
 */

interface MatterLike {
  id: number;
  name: string;
  group_name?: string | null;
  status?: string;
  fee_sharing_configs?: {
    is_active?: boolean;
    participants?: { client: number }[];
  }[];
}

interface ClientLike {
  id: number;
  name: string;
  email?: string | null;
  vat_number?: string | null;
}

interface UserLike {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
}

export function matterLabel(m: { name: string; group_name?: string | null }): string {
  return m.group_name ? `${m.group_name} - ${m.name}` : m.name;
}

const NONE = 'none';

interface EntityComboboxBaseProps {
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  /** Adds a first option (e.g. "No matter", "Auto") that maps to null. */
  noneLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
}

interface MatterComboboxProps extends EntityComboboxBaseProps {
  /** Restrict the choices; defaults to all matters from the store. */
  matters?: MatterLike[];
  includeInactive?: boolean;
}

export function MatterCombobox({
  value,
  onChange,
  matters,
  includeInactive = false,
  noneLabel,
  placeholder = 'Select matter...',
  disabled,
  triggerClassName,
}: MatterComboboxProps) {
  const storeMatters = useStore((s) => s.matters);
  const storeClients = useStore((s) => s.clients);

  const options = useMemo(() => {
    const clientNameById = new Map(storeClients.map((c) => [c.id, c.name]));
    const list = (matters ?? storeMatters).filter(
      (m) => includeInactive || m.status === 'active' || !m.status,
    );
    const opts = list
      .map((m) => {
        const config =
          m.fee_sharing_configs?.find((c) => c.is_active) || m.fee_sharing_configs?.[0];
        const clientNames = (config?.participants ?? [])
          .map((p) => clientNameById.get(p.client) || '')
          .join(' ');
        return {
          value: String(m.id),
          label: matterLabel(m),
          searchableText: `${m.name} ${m.group_name || ''} ${clientNames}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return noneLabel ? [{ value: NONE, label: noneLabel }, ...opts] : opts;
  }, [matters, storeMatters, storeClients, includeInactive, noneLabel]);

  return (
    <ComboboxSelect
      options={options}
      value={value ? String(value) : noneLabel ? NONE : ''}
      onValueChange={(v) => onChange(v === NONE ? null : Number(v))}
      placeholder={placeholder}
      searchPlaceholder="Search matters..."
      emptyMessage="No matters found."
      disabled={disabled}
      triggerClassName={triggerClassName}
      preserveOrder
    />
  );
}

interface ClientComboboxProps extends EntityComboboxBaseProps {
  /** Restrict the choices; defaults to all clients from the store. */
  clients?: ClientLike[];
}

export function ClientCombobox({
  value,
  onChange,
  clients,
  noneLabel,
  placeholder = 'Select client...',
  disabled,
  triggerClassName,
}: ClientComboboxProps) {
  const storeClients = useStore((s) => s.clients);

  const options = useMemo(() => {
    const opts = (clients ?? storeClients)
      .map((c) => ({
        value: String(c.id),
        label: c.name,
        searchableText: `${c.name} ${c.email || ''} ${c.vat_number || ''}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return noneLabel ? [{ value: NONE, label: noneLabel }, ...opts] : opts;
  }, [clients, storeClients, noneLabel]);

  return (
    <ComboboxSelect
      options={options}
      value={value ? String(value) : noneLabel ? NONE : ''}
      onValueChange={(v) => onChange(v === NONE ? null : Number(v))}
      placeholder={placeholder}
      searchPlaceholder="Search clients..."
      emptyMessage="No clients found."
      disabled={disabled}
      triggerClassName={triggerClassName}
      preserveOrder
    />
  );
}

interface UserComboboxProps extends EntityComboboxBaseProps {
  /** Restrict the choices; defaults to all users from the store. */
  users?: UserLike[];
}

export function UserCombobox({
  value,
  onChange,
  users,
  noneLabel,
  placeholder = 'Select user...',
  disabled,
  triggerClassName,
}: UserComboboxProps) {
  const storeUsers = useStore((s) => s.users);

  const options = useMemo(() => {
    const opts = (users ?? storeUsers)
      .map((u) => ({
        value: String(u.id),
        label: `${u.first_name} ${u.last_name}`.trim(),
        searchableText: `${u.first_name} ${u.last_name} ${u.email || ''}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return noneLabel ? [{ value: NONE, label: noneLabel }, ...opts] : opts;
  }, [users, storeUsers, noneLabel]);

  return (
    <ComboboxSelect
      options={options}
      value={value ? String(value) : noneLabel ? NONE : ''}
      onValueChange={(v) => onChange(v === NONE ? null : Number(v))}
      placeholder={placeholder}
      searchPlaceholder="Search users..."
      emptyMessage="No users found."
      disabled={disabled}
      triggerClassName={triggerClassName}
      preserveOrder
    />
  );
}
